import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Col, Row } from 'reactstrap';
import { TooNarrowMessage, WidgetContainer } from '../../../styledComponents/styledComponents';

import useWindowDimensions from '../../../hooks/useWindowDimensions';
import PropTypes from 'prop-types';

import MainInputs from './MainInputs';
import TableTypeTabs from './TableTypeTabs';
import TableView from './TableView';

import {
   misuseTimespanSelector,
   misuseFilterSelector,
   misuseAggTermSelector,
   misuseCcStateSelector,
   hashSelector,
} from '../../../redux/selectors/selectors';

import { setMisuseAggTerm, setMisuseCcState, setMisuseFilter, setMisuseTimespan } from '../../../redux/actions/general';
import { URL_PARAMS } from '../../../urlSync/urlParamNames';

import { Filter } from '../../common';
import { DefaultGrey } from '../../../constants/colors';
import * as browserStorage from '../../../misc/BrowserStorageManager';
import useSignal from '../../../hooks/useSignal';

function MisuseContainer({ location, history }) {
   const dispatch = useDispatch();
   const userInfo = browserStorage.getUserInfo();
   const { width } = useWindowDimensions();

   const orgahash = useSelector(hashSelector);

   const storeFilter = useSelector(misuseFilterSelector);
   const storeAggTerm = useSelector(misuseAggTermSelector);
   const storeCcState = useSelector(misuseCcStateSelector);
   const { start, end } = useSelector(misuseTimespanSelector);

   const [localFilter, setLocalFilter] = useState(storeFilter);
   const [localStart, setLocalStart] = useState(start);
   const [localEnd, setLocalEnd] = useState(end);
   const [concurrencyIsDesired, setConcurrencyState] = useState(storeCcState);

   const [aggTerm, setAggTerm] = useState(storeAggTerm);

   const [isLoading, setLoadingState] = useState(false);
   const [requestTrigger, activateTriggerRequest] = useState(false);
   const [hasTableRequestStarted, activateTableRequestStart] = useState(false);
   const [manualDataFetch, triggerDataFetchManually] = useSignal();

   const [hasRequestCancelled, setHasRequestCancelled] = useState(false);

   const [filteringChanged, setFilteringChangedState] = useState(false);

   const localFilterRef = useRef();
   localFilterRef.current = localFilter;
   const localStartRef = useRef();
   localStartRef.current = localStart;
   const localEndRef = useRef();
   localEndRef.current = localEnd;
   const aggTermRef = useRef();
   aggTermRef.current = aggTerm;
   const ccStateRef = useRef();
   ccStateRef.current = concurrencyIsDesired;

   const setStreams = useCallback((v) => setLocalFilter((f) => ({ ...f, streams: v })), []);
   const setIps = useCallback((v) => setLocalFilter((f) => ({ ...f, ips: v })), []);
   const setJtis = useCallback((v) => setLocalFilter((f) => ({ ...f, jtis: v })), []);
   const setReferrers = useCallback((v) => setLocalFilter((f) => ({ ...f, referrers: v })), []);
   const setCountries = useCallback((v) => setLocalFilter((f) => ({ ...f, countries: v })), []);
   const setProviders = useCallback((v) => setLocalFilter((f) => ({ ...f, providers: v })), []);
   const setUsers = useCallback((v) => setLocalFilter((f) => ({ ...f, users: v })), []);
   const setTags = useCallback((v) => setLocalFilter((f) => ({ ...f, tags: v })), []);

   useEffect(() => {
      const filterKeys = ['streams', 'ips', 'jtis', 'referrers', 'countries', 'providers', 'users', 'tags'];
      const toValues = (arr) =>
         (arr ?? [])
            .map((x) => x.value)
            .sort()
            .join(',');
      const hasFilterChanged = filterKeys.some((key) => toValues(storeFilter[key]) !== toValues(localFilter[key]));
      const hasTimespanChanged = !start.isSame(localStart) || !end.isSame(localEnd);
      const hasCcStateChanged = storeCcState !== concurrencyIsDesired;
      setFilteringChangedState(hasFilterChanged || hasTimespanChanged || hasCcStateChanged);
   }, [start, end, storeFilter, storeCcState, localStart, localEnd, localFilter, concurrencyIsDesired]);

   // activate initial URL sync
   useEffect(() => {
      const newTimespan = { start: localStartRef.current, end: localEndRef.current };
      dispatch(
         setMisuseTimespan(newTimespan, {
            [URL_PARAMS.MISUSE_START]: newTimespan.start,
            [URL_PARAMS.MISUSE_END]: newTimespan.end,
         }),
      );
   }, [dispatch]);

   // requestTrigger === true => inputs are being applied
   useEffect(() => {
      if (requestTrigger) {
         const newFilter = { ...localFilterRef.current };
         dispatch(
            setMisuseFilter(newFilter, {
               [URL_PARAMS.MISUSE_STREAMS]: newFilter.streams,
               [URL_PARAMS.MISUSE_IPS]: newFilter.ips,
               [URL_PARAMS.MISUSE_JTIS]: newFilter.jtis,
               [URL_PARAMS.MISUSE_REFERRERS]: newFilter.referrers,
               [URL_PARAMS.MISUSE_COUNTRIES]: newFilter.countries,
               [URL_PARAMS.MISUSE_PROVIDERS]: newFilter.providers,
               [URL_PARAMS.MISUSE_USERS]: newFilter.users,
               [URL_PARAMS.MISUSE_TAGS]: newFilter.tags,
            }),
         );
         const newTimespan = { start: localStartRef.current, end: localEndRef.current };
         dispatch(
            setMisuseTimespan(newTimespan, {
               [URL_PARAMS.MISUSE_START]: newTimespan.start,
               [URL_PARAMS.MISUSE_END]: newTimespan.end,
            }),
         );
         dispatch(setMisuseAggTerm(aggTermRef.current, { [URL_PARAMS.MISUSE_AGG_TERM]: aggTermRef.current }));
         dispatch(setMisuseCcState(ccStateRef.current, { [URL_PARAMS.MISUSE_CC_STATE]: ccStateRef.current }));
         setHasRequestCancelled(false);
      }
   }, [requestTrigger, dispatch]);

   // changed inputs triggers data http requests
   useEffect(() => {
      activateTableRequestStart(true);
      setHasRequestCancelled(false);
   }, [storeFilter, start, end, orgahash, manualDataFetch]);

   // http request in table view is completed
   const handleTableRequestCompleted = useCallback(() => {
      activateTriggerRequest(false);
      activateTableRequestStart(false);
   }, []);

   return width < 350 ? (
      <TooNarrowMessage>{'Please enlarge the window to use IP filtering'}</TooNarrowMessage>
   ) : (
      <>
         <Row>
            <Col>{userInfo.isAdmin && <Filter history={history} location={location} />}</Col>
         </Row>

         <WidgetContainer $bgColor={DefaultGrey(1, 247)}>
            <Row>
               <Col>
                  <MainInputs
                     localFilter={localFilter}
                     setStreams={setStreams}
                     setIps={setIps}
                     setJtis={setJtis}
                     setReferrers={setReferrers}
                     setCountries={setCountries}
                     setProviders={setProviders}
                     setUsers={setUsers}
                     setTags={setTags}
                     start={localStart}
                     setStart={setLocalStart}
                     end={localEnd}
                     setEnd={setLocalEnd}
                     hasTableRequestStarted={hasTableRequestStarted}
                     activateTriggerRequest={activateTriggerRequest}
                     isLoading={isLoading}
                     concurrencyIsDesired={concurrencyIsDesired}
                     setConcurrencyState={setConcurrencyState}
                     filteringChanged={filteringChanged}
                  />
               </Col>
            </Row>

            <TableTypeTabs
               aggTerm={aggTerm}
               setAggTerm={setAggTerm}
               activateTriggerRequest={activateTriggerRequest}
               disabled={hasTableRequestStarted}
            />

            <TableView
               start={start}
               end={end}
               misuseFilter={storeFilter}
               hasRequestStarted={hasTableRequestStarted}
               handleRequestCompleted={handleTableRequestCompleted}
               hasRequestCancelled={hasRequestCancelled}
               setHasRequestCancelled={setHasRequestCancelled}
               triggerDataFetchManually={triggerDataFetchManually}
               setLoadingState={setLoadingState}
               concurrencyIsDesired={storeCcState}
               aggTerm={storeAggTerm}
            />
         </WidgetContainer>
      </>
   );
}

export default MisuseContainer;

MisuseContainer.propTypes = {
   location: PropTypes.object,
   history: PropTypes.object,
};
